import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ArrowDown, Star } from 'lucide-react';
interface HeroProps {
  onScrollToPurchase: () => void;
}
export function Hero({
  onScrollToPurchase
}: HeroProps) {
  const {
    t
  } = useLanguage();
  return <motion.section initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.6
  }} className="relative min-h-screen flex items-center justify-center px-4 py-12 md:py-20" data-id="element-20">
      <div className="max-w-5xl mx-auto text-center" data-id="element-21">
        {/* Título principal - Hook inmediato con número destacado */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-[#2C4A3E] mb-6 md:mb-8 leading-tight px-2" data-id="element-22">
          <span className="text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold text-[#8B4513]" data-id="element-23">
            7
          </span>{' '}
          Pasos para Sanar tu Relación con el Dinero
        </motion.h1>

        {/* Tagline - Debajo del título en color terracota */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="text-xl md:text-2xl lg:text-3xl text-[#8B4513] font-serif italic mb-10 md:mb-12 px-2" data-id="element-24">
          {t.hero.tagline}
        </motion.p>

        {/* Portada del libro - ELEMENTO VISUAL DOMINANTE */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} className="mb-8 md:mb-10" data-id="element-25">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3eue4QpepZ2rvNVUGT8Lok/ChatGPT_Image_23_nov_2025%2C_20_10_54.png" alt="7 Pasos para Sanar tu Relación con el Dinero" className="w-full max-w-md md:max-w-lg mx-auto shadow-2xl rounded-sm" loading="eager" data-id="element-26" />
        </motion.div>

        {/* Social proof - Reviews (validación inmediata) */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="flex items-center justify-center gap-3 mb-10 md:mb-12" data-id="element-27">
          <div className="flex gap-1" data-id="element-28">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 md:w-6 md:h-6 fill-[#D4A574] text-[#D4A574]" data-id="element-29" />)}
          </div>
          <span className="text-base md:text-lg text-[#2C4A3E] font-semibold" data-id="element-30">
            1,280 reviews
          </span>
        </motion.div>

        {/* CTA button - Justo debajo de reviews */}
        <motion.button initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        delay: 0.5
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.98
      }} onClick={onScrollToPurchase} className="relative bg-gradient-to-br from-[#E8B86D] via-[#D4A574] to-[#C87941] text-[#2C4A3E] px-10 md:px-14 py-5 md:py-6 rounded-full text-lg md:text-xl font-bold transition-all duration-300 shadow-xl hover:shadow-2xl mb-12 md:mb-16" data-id="element-31">
          <span className="relative z-10" data-id="element-32">{t.hero.cta}</span>
        </motion.button>

        {/* Copy introductorio - Justo antes del preview en audio */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mb-8 md:mb-10 max-w-3xl mx-auto" data-id="element-33">
          <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] font-semibold mb-5" data-id="element-34">
            {t.intro.greeting}
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-35">
            {t.intro.p1}
          </p>
        </motion.div>

        {/* Preview en Audio - Engagement interactivo */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.7
      }} className="mb-0 max-w-lg mx-auto" data-id="element-36">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-37">
            <p className="text-sm uppercase tracking-wider text-[#2C4A3E] font-bold" data-id="element-38">
              Escucha un adelanto
            </p>
            <ArrowDown className="w-4 h-4 text-[#2C4A3E]" strokeWidth={2.5} data-id="element-39" />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-lg bg-gradient-to-r from-[#2C4A3E] to-[#1F3329] border border-[#D4A574]/30 p-4" data-id="element-40">
            <div className="flex items-center gap-3" data-id="element-41">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1MtNcpfonXus3L7q1VYDbV/ChatGPT_Image_23_nov_2025%2C_20_10_54.png" alt="Album artwork" className="w-12 h-12 rounded-full shadow-md object-cover flex-shrink-0 ring-2 ring-[#D4A574]/50" loading="lazy" data-id="element-42" />
              <audio controls preload="auto" className="flex-1 h-10" style={{
              maxWidth: '100%',
              accentColor: '#2C4A3E'
            }} data-id="element-43">
                <source src="https://www.dropbox.com/scl/fi/l15vzwjzkl1rwps6orxtp/wendy-def-1.mp3?rlkey=rox17t1hsa8oxcogrr3c4nait&st=by35vcm1&raw=1" type="audio/mpeg" data-id="element-44" />
                Tu navegador no soporta el elemento de audio.
              </audio>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>;
}